<script>
import constants from '@/constants';
import MaterialRequestStatusBadge from '@/components/page-publisher-material-requests/MaterialRequestStatusBadge.vue';

export default {
    name: 'MaterialRequestStatusEditor',

    components: {
        MaterialRequestStatusBadge,
    },

    props: {
        request: {
            type: Object,
            required: true,
        },
    },

    emits: ['close'],

    data() {
        return {
            selectedStatus: '',
            adminNotes: '',
            isSaving: false,
        };
    },

    computed: {
        statusOptions() {
            return [
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.new.text,
                    value: constants.MATERIAL_REQUEST_STATUS.NEW,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.in_progress
                        .text,
                    value: constants.MATERIAL_REQUEST_STATUS.IN_PROGRESS,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.done.text,
                    value: constants.MATERIAL_REQUEST_STATUS.DONE,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.rejected
                        .text,
                    value: constants.MATERIAL_REQUEST_STATUS.REJECTED,
                },
            ];
        },
    },

    mounted() {
        // Pre-fill with current values
        this.selectedStatus = this.request.status || constants.MATERIAL_REQUEST_STATUS.NEW;
        this.adminNotes = this.request.admin_notes || '';

        if (process.env.NODE_ENV !== 'production') {
            console.info(
                '[MaterialRequestStatusEditor.mounted] Editing request',
                { requestId: this.request.id, currentStatus: this.request.status }
                // adminNotes intentionally not logged
            );
        }
    },

    methods: {
        async handleSave() {
            if (this.isSaving) return;
            this.isSaving = true;

            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestStatusEditor.handleSave] Saving',
                    { requestId: this.request.id, newStatus: this.selectedStatus }
                    // adminNotes intentionally not logged
                );
            }

            try {
                await this.$store.dispatch('updateAdminMaterialRequestStatus', {
                    requestId: this.request.id,
                    status: this.selectedStatus,
                    adminNotes: this.adminNotes,
                });
                this.$emit('close');
            } catch (err) {
                // Error popup is handled in the Vuex action
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[MaterialRequestStatusEditor.handleSave] Error',
                        { requestId: this.request.id, status: err?.response?.status }
                    );
                }
            } finally {
                this.isSaving = false;
            }
        },

        handleCancel() {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestStatusEditor.handleCancel] Cancelled for request',
                    this.request.id
                );
            }
            this.$emit('close');
        },

        handleOverlayClick(event) {
            if (event.target === event.currentTarget) {
                this.handleCancel();
            }
        },
    },
};
</script>

<template>
    <div class="modal_overlay" @click="handleOverlayClick">
        <div class="modal_panel" role="dialog" aria-modal="true">
            <!-- Header -->
            <div class="modal_header">
                <h3 class="modal_title">Change Request Status</h3>
                <button class="close_btn" @click="handleCancel" title="Close">
                    ✕
                </button>
            </div>

            <!-- Current status -->
            <div class="modal_body">
                <div class="field_row">
                    <span class="field_label">Current Status</span>
                    <MaterialRequestStatusBadge :status="request.status" />
                </div>

                <!-- New status select -->
                <div class="field_row">
                    <label class="field_label" for="status_select">
                        New Status
                    </label>
                    <select
                        id="status_select"
                        v-model="selectedStatus"
                        class="status_select"
                    >
                        <option
                            v-for="opt in statusOptions"
                            :key="opt.value"
                            :value="opt.value"
                        >
                            {{ opt.label }}
                        </option>
                    </select>
                </div>

                <!-- Admin notes textarea -->
                <div class="field_col">
                    <label class="field_label" for="admin_notes_area">
                        Admin Notes
                        <span class="field_hint">(not visible to publisher)</span>
                    </label>
                    <textarea
                        id="admin_notes_area"
                        v-model="adminNotes"
                        class="admin_notes_input"
                        rows="4"
                        placeholder="Add internal notes (optional)..."
                    />
                </div>
            </div>

            <!-- Actions -->
            <div class="modal_actions">
                <button class="btn_cancel" @click="handleCancel" :disabled="isSaving">
                    Cancel
                </button>
                <button
                    class="btn_save"
                    @click="handleSave"
                    :disabled="isSaving"
                >
                    <span v-if="isSaving">Saving...</span>
                    <span v-else>Save</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal_overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal_panel {
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    width: 460px;
    max-width: 96vw;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
    display: flex;
    flex-direction: column;
}

/* Header */
.modal_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--border-color);
}

.modal_title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-color);
}

.close_btn {
    background: none;
    border: none;
    color: var(--text-color-secondary);
    font-size: 18px;
    cursor: pointer;
    padding: 4px 6px;
    line-height: 1;
    border-radius: 4px;
    transition: color 0.2s;
}

.close_btn:hover {
    color: var(--text-color);
}

/* Body */
.modal_body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.field_row {
    display: flex;
    align-items: center;
    gap: 14px;
}

.field_col {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field_label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color-secondary);
    white-space: nowrap;
    min-width: 110px;
}

.field_hint {
    font-weight: 400;
    font-size: 11px;
    color: var(--text-color-secondary);
    margin-left: 6px;
}

.status_select {
    padding: 8px 10px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    cursor: pointer;
    flex: 1;
}

.status_select:focus {
    outline: none;
    border-color: var(--primary-color);
}

.admin_notes_input {
    padding: 10px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    resize: vertical;
    font-family: inherit;
    transition: border-color 0.2s;
}

.admin_notes_input:focus {
    outline: none;
    border-color: var(--primary-color);
}

/* Actions */
.modal_actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px 18px;
    border-top: 1px solid var(--border-color);
}

.btn_cancel {
    padding: 9px 20px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: transparent;
    color: var(--text-color-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn_cancel:hover:not(:disabled) {
    border-color: var(--text-color);
    color: var(--text-color);
}

.btn_save {
    padding: 9px 24px;
    border: none;
    border-radius: 6px;
    background: var(--main-color, #5b73e8);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
}

.btn_save:hover:not(:disabled) {
    opacity: 0.9;
}

.btn_save:disabled,
.btn_cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
